import { type Battle, type BattleRoomCopy, battleFairnessReadout } from "@loot-os/api-contract";
import type { BattleFairnessContent } from "@loot-os/design-system/templates/battles/battlesContent";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { seedQueryOptions } from "../../../../../lib/queries.js";
import { useTerminology } from "../../../../../lib/terminology.js";

/**
 * The battle's fairness inputs: its salt, the daily seed's commitment (and the seed once its day
 * has passed), and every revealed open's client seed, nonce and result, so any of them can be
 * recomputed. Nonces are battle-local: `round × seats + seat`. The seed is fetched once shown.
 * The section's own words are the store's default copy (`copy`); what it states of the rolls and
 * their inputs is the platform's (`battleFairnessReadout`).
 */
export function useBattleFairnessContent(
  battle: Battle | null,
  copy: BattleRoomCopy["fairness"],
): BattleFairnessContent | null {
  const [open, setOpen] = useState(false);
  const readout = battleFairnessReadout(useTerminology());
  const seed = useQuery({
    ...seedQueryOptions(battle?.seedDate ?? "none"),
    enabled: open && !!battle?.seedDate,
  });
  if (!battle) return null;
  return {
    title: copy.title,
    description: readout.description,
    toggle: {
      open,
      showLabel: copy.show,
      hideLabel: copy.hide,
      onToggle: () => setOpen((v) => !v),
    },
    facts: [
      { label: readout.facts.salt, value: battle.salt },
      { label: readout.facts.seedDate, value: battle.seedDate ?? readout.notStarted },
      { label: readout.facts.seedHash, value: seed.data?.seedHash ?? readout.loading },
      {
        label: readout.facts.seed,
        value: seed.data ? (seed.data.seed ?? readout.seedHidden) : readout.loading,
      },
    ],
    opens: {
      caption: copy.caption,
      headers: readout.headers,
      rows: battle.results.flatMap((round) =>
        round.map((o) => ({
          key: `${o.roundIndex}-${o.seatIndex}`,
          round: String(o.roundIndex + 1),
          seat: String(o.seatIndex + 1),
          clientSeed: battle.seats[o.seatIndex]?.clientSeed ?? readout.noClientSeed,
          nonce: String(o.nonce),
          result: String(o.result),
          item: readout.pull(o.item.name, o.itemIndex),
        })),
      ),
      emptyLabel: copy.empty,
    },
    verification:
      battle.status === "completed"
        ? {
            label: copy.verification,
            href: `/api/loot/v1/battles/${battle.id}/verification`,
          }
        : null,
  };
}
